import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { useSyncExternalStore } from 'react';
import { baseUrl } from '../constatnts/Connections';

// Backend na Render-u se uspava pa prvi zahtev može da traje i do ~1 minut.
// Ako zahtev traje duže od SLOW_AFTER_MS, prikazuje se modal (ServerWakeupModal),
// a GET zahtevi koji ne uspeju zbog buđenja servera automatski se ponavljaju.
const SLOW_AFTER_MS = 4000;
const RETRY_EVERY_MS = 5000;
const GIVE_UP_AFTER_MS = 90000;

type WakeupExtra = { __wid?: number; __wstart?: number; __wtimer?: ReturnType<typeof setTimeout> };
type WakeupConfig = AxiosRequestConfig & WakeupExtra;
type InternalWakeupConfig = InternalAxiosRequestConfig & WakeupExtra;

type WakeupState = { visible: boolean; since: number | null; gaveUp: boolean };

let state: WakeupState = { visible: false, since: null, gaveUp: false };
const slowIds = new Set<number>();
const listeners = new Set<() => void>();
let nextId = 1;
let installed = false;

const setState = (next: WakeupState) => {
    state = next;
    listeners.forEach((l) => l());
};

const refresh = () => {
    if (state.gaveUp) return;
    const visible = slowIds.size > 0;
    if (visible === state.visible) return;
    setState({ visible, since: visible ? Date.now() : null, gaveUp: false });
};

const markSlow = (id: number) => {
    slowIds.add(id);
    refresh();
};

const settle = (config?: WakeupConfig) => {
    if (!config) return;
    if (config.__wtimer) clearTimeout(config.__wtimer);
    if (config.__wid) slowIds.delete(config.__wid);
    refresh();
};

const isOurs = (config: WakeupConfig) => {
    const url = config.url || '';
    return url.startsWith(baseUrl) || url.startsWith('/') || !/^https?:\/\//.test(url) || (config.baseURL || '').startsWith(baseUrl);
};

const isWakingUp = (error: AxiosError) => {
    const response = error.response;
    if (!response && error.code === 'ERR_CANCELED') return false;
    const status = response?.status;
    return !response || status === 502 || status === 503 || status === 504;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function installServerWakeup() {
    if (installed) return;
    installed = true;

    axios.interceptors.request.use((config: InternalWakeupConfig) => {
        if (!isOurs(config)) return config;
        if (!config.__wid) config.__wid = nextId++;
        if (!config.__wstart) config.__wstart = Date.now();
        const id = config.__wid;
        config.__wtimer = setTimeout(() => markSlow(id), Math.max(0, SLOW_AFTER_MS - (Date.now() - config.__wstart)));
        return config;
    });

    axios.interceptors.response.use(
        (response) => {
            settle(response.config as WakeupConfig);
            return response;
        },
        async (error: AxiosError) => {
            const config = error.config as WakeupConfig | undefined;
            if (!config || !config.__wid) return Promise.reject(error);
            if (config.__wtimer) clearTimeout(config.__wtimer);

            const isGet = (config.method || 'get').toLowerCase() === 'get';
            const elapsed = Date.now() - (config.__wstart || Date.now());

            if (isGet && isWakingUp(error)) {
                if (elapsed < GIVE_UP_AFTER_MS) {
                    markSlow(config.__wid);
                    await sleep(RETRY_EVERY_MS);
                    return axios(config);
                }
                // server se nije probudio u zadatom roku: modal ostaje sa dugmetom „Pokušaj ponovo“
                slowIds.delete(config.__wid);
                setState({ visible: true, since: state.since, gaveUp: true });
                return Promise.reject(error);
            }

            settle(config);
            return Promise.reject(error);
        }
    );
}

const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

export function useServerWakeup() {
    return useSyncExternalStore(subscribe, () => state);
}
